import { useQueries } from '@tanstack/react-query'
import { PaginatedResponse, PaginationParams } from '@pubstack/common/src/pagination'
import { CreateSite, SiteWithStacks, SiteControlCenterStatus, SiteEnv, SiteProduct, SiteProductFilter, SiteStatus, Site } from '@pubstack/common/src/site'
import { sortByName } from '@pubstack/common/src/sort'
import { Product } from '~/hooks/useProduct'
import { buildUrlWithSearchParams } from '~/utils/url'
import { get, MutationToasts, useInfiniteQuery, useMutation, useQuery } from '../api-client'

export const SITES_QUERY_KEYS = (scopeId: string) => [scopeId, 'sites']
const ENDPOINT = '/sites'

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useSites = (scopeId: string, search?: string) => {
  const url = buildUrlWithSearchParams(ENDPOINT, { search })

  const queryResult = useQuery<Site[]>({ url, keys: [...SITES_QUERY_KEYS(scopeId), { search }], select: sortByName })
  return { ...queryResult, data: queryResult.data || [] }
}

export const useSite = (siteId: string, scopeId: string) => {
  return useQuery<Site>({ url: `${ENDPOINT}/${siteId}`, keys: [...SITES_QUERY_KEYS(scopeId), siteId], enabled: !!siteId })
}

export const useSitesByIds = (siteIds: string[], scopeId: string) => {
  return useQueries({
    queries: siteIds.map((siteId) => ({ queryKey: [...SITES_QUERY_KEYS(scopeId), siteId], queryFn: () => get<Site>(`${ENDPOINT}/${siteId}`) })),
    combine: (results) => ({ data: results.flatMap((result) => (result.data ? [result.data] : [])), isLoading: results.some((result) => result.isLoading) }),
  })
}

export const useSitesWithPagination = ({
  scopeId,
  filters,
  pagination,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  scopeId: string
  filters?: {
    pbsStandaloneStatus?: SiteStatus[]
    controlCenterStatus?: SiteControlCenterStatus
    search: string | null
    admStatus?: SiteStatus[]
    /** The status the media shows, whichever product it comes from — what the unified media list filters on. */
    mediaStatus?: SiteStatus[]
    /** Scopes by stacks, for the deployment center. */
    product?: Product
    /**
     * Scopes by activation: a product's medias plus the Analytics ones, or `'any'` for the medias
     * activated on either product — what a server-side bidder seat maps over.
     */
    siteProduct?: SiteProductFilter
    /** The environments to narrow to. */
    env?: SiteEnv[]
    /**
     * The product to narrow to, read the way the media list's Product column reads them — the single
     * product a media runs on, which is not the same question `siteProduct` asks.
     */
    mediaProduct?: SiteProduct[]
    /**
     * The archived medias instead of the live ones — never both, so the media list cannot show a row
     * the archived list is meant to hold. Owner-gated server-side, so asking for it is not enough.
     */
    archived?: boolean
  }
  pagination: PaginationParams
  sortBy?: string
  sortOrder?: 'ascending' | 'descending'
  enabled?: boolean
}) => {
  const url = buildUrlWithSearchParams(`${ENDPOINT}/pagination`, {
    ...filters,
    ...pagination,
    sortBy,
    sortOrder,
  })

  const queryResult = useQuery<PaginatedResponse<SiteWithStacks>>({
    url,
    keys: [...SITES_QUERY_KEYS(scopeId), { pagination, sortBy, sortOrder, filters }],
    enabled,
    keepPreviousDataEnabled: true,
  })
  return { ...queryResult, data: queryResult.data || { data: [], pagination: { total: 0, totalPages: 1, ...pagination } } }
}

export const useInfiniteSites = ({
  scopeId,
  filters,
  limit,
  sortBy,
  sortOrder,
  enabled = true,
}: {
  scopeId: string
  filters?: {
    pbsStandaloneStatus?: SiteStatus[]
    search: string | null
    mappedBidderSeatId?: string
    isMapped?: boolean
    admStatus?: SiteStatus[]
    /** See `useSitesWithPagination`: activation rather than stacks. */
    siteProduct?: SiteProductFilter
    /** See `useSitesWithPagination`: the single product a media runs on, the Product column's question. */
    mediaProduct?: SiteProduct[]
  }
  limit: number
  sortBy?: string
  sortOrder?: 'ascending' | 'descending'
  enabled?: boolean
}) => {
  const queryResult = useInfiniteQuery<PaginatedResponse<SiteWithStacks>>({
    url: `${ENDPOINT}/pagination`,
    keys: [...SITES_QUERY_KEYS(scopeId), 'infinite', { filters, limit, sortBy, sortOrder }],
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.pagination.page >= lastPage.pagination.totalPages) {
        return undefined
      }
      return lastPageParam + 1
    },
    buildUrl: (baseUrl, page) =>
      buildUrlWithSearchParams(baseUrl, {
        ...filters,
        page,
        limit,
        sortBy,
        sortOrder,
      }),
  })

  return { ...queryResult, data: queryResult.data || { pages: [], pageParams: [] } }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateSites = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<CreateSite[], Site[]>({
    method: 'post',
    url: ENDPOINT,
    collectionKeys: SITES_QUERY_KEYS(scopeId),
    ...toasts,
  })
}

export const useUpdateSite = (scopeId: string, siteId: string, toasts: MutationToasts = {}) => {
  return useMutation<Site>({
    method: 'put',
    url: `${ENDPOINT}/${siteId}`,
    collectionKeys: SITES_QUERY_KEYS(scopeId),
    resourceId: siteId,
    isOptimistic: true,
    ...toasts,
  })
}
